import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';

import type { PatientIntakeStep, StatusActivityStatus, StepIdentifier } from '@shared/patient-intake-status';

import { StatusIcon } from '../components';
import { getStatusColour } from '../utils';
import { getStepConfig } from '../getStepConfig';
import { usePatientIntakeStatusContext } from '../hooks';
import type { CompleteStepFormData } from './CompleteStepDialog.tsx';

const STATUS_OPTIONS = {
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  NOT_STARTED: 'NOT_STARTED',
  ERROR: 'ERROR',
} satisfies {
  [K in StatusActivityStatus]: K;
};

type StatusSelectionLabelProps = {
  label: string;
  statusContext: React.ReactNode;
};

const StatusSelectionLabel: React.FC<StatusSelectionLabelProps> = ({ label, statusContext }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
    justifyContent="space-between"
    gap={{ xs: 0, sm: 2 }}
  >
    <Typography variant="body2" sx={{ textWrap: 'nowrap' }}>
      {label}
    </Typography>
    <Typography variant="caption" textAlign={{ xs: 'left', sm: 'right' }}>
      {statusContext}
    </Typography>
  </Stack>
);

interface StatusSelectionControllerProps extends React.ComponentProps<typeof Radio> {
  currentStatus: StatusActivityStatus;
  stepIdentifier: StepIdentifier;
}

const StatusSelectionController = ({ currentStatus, stepIdentifier, ...props }: StatusSelectionControllerProps) => {
  const { colour } = getStatusColour(currentStatus);

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <StatusIcon status={currentStatus} stepIdentifier={stepIdentifier} fontSize="small" />
      <Radio size="small" color={colour === 'inherit' ? 'default' : colour} sx={{ p: 0 }} {...props} />
    </Stack>
  );
};

type StatusSelectionProps = {
  stepIdentifier: StepIdentifier;
  currentStatus: StatusActivityStatus;
};

const StatusSelection: React.FC<StatusSelectionProps> = ({ stepIdentifier, currentStatus }) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();
  const stepConfig = getStepConfig(stepIdentifier);

  const { label, context } = stepConfig.statuses[currentStatus];

  const statusContext = context({ isHealthcareProvider });

  return (
    <FormControlLabel
      value={currentStatus}
      control={<StatusSelectionController currentStatus={currentStatus} stepIdentifier={stepIdentifier} />}
      sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1 }}
      label={<StatusSelectionLabel label={label} statusContext={statusContext} />}
      slotProps={{
        typography: {
          sx: {
            width: '100%',
          },
        },
      }}
    />
  );
};

type StatusSelectorFormSectionProps = {
  step: PatientIntakeStep;
  control: Control<CompleteStepFormData>;
};

export const StatusSelectorFormSection: React.FC<StatusSelectorFormSectionProps> = ({ control, step }) => (
  <Controller
    name="status"
    control={control}
    render={({ field }) => (
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <Typography variant="subtitle2" gutterBottom>
          Update Status
        </Typography>
        <RadioGroup {...field} sx={{ width: '100%', gap: 2 }}>
          {Object.entries(STATUS_OPTIONS).map(([status, value]) => (
            <StatusSelection key={status} stepIdentifier={step.type} currentStatus={value} />
          ))}
        </RadioGroup>
      </FormControl>
    )}
  />
);
